import { isValidUuid, Locales, QuestionType } from "@simplyk/common";
import { inferRouterInputs, inferRouterOutputs } from "@trpc/server";
import { v4 } from "uuid";

import { DonationFormQuestionRowV4 } from "../types";

import { AppRouterType } from "@/common/helpers/trpc";
import { Translate } from "@/common/hooks/useTranslate";
import { QuestionOutput } from "@/common/types/trpc";

type QuestionStepDataItem = inferRouterOutputs<AppRouterType>["getQuestionsStepData"]["questions"][number];
type QuestionInputV3 = inferRouterInputs<AppRouterType>["updateQuestionsV3"]["questions"][number];

export const mapApiDataToRows = (apiQuestions: QuestionStepDataItem[]): DonationFormQuestionRowV4[] =>
  apiQuestions.map((item) => ({
    localId: v4(),
    questionId: item.questionId,
    crmCustomPropertyId: item.crmCustomPropertyId,
    body: item.body,
    type: item.type,
    required: item.required,
    scope: "buyer",
    choices: item.choices.map((c) => ({ id: c.id, body: c.body, sortIndex: c.sortIndex })),
    sortIndex: item.sortIndex,
    isActiveOnForm: item.isLinkedToForm,
    isNew: false,
    isDirty: false,
  }));

export const toV3Input = (rows: DonationFormQuestionRowV4[]): QuestionInputV3[] =>
  rows
    .filter((row) => row.isActiveOnForm)
    .map((row) => ({
      id: row.questionId ?? undefined,
      crmCustomPropertyId: row.crmCustomPropertyId ?? undefined,
      body: row.body,
      type: row.type,
      required: row.required,
      choices: row.choices.map((c) => ({
        id: row.questionId && c.id && isValidUuid(c.id) ? c.id : undefined,
        body: c.body,
      })),
      scope: "buyer",
    }));
export const restoreNotificationPhoneQuestion = (
  rows: DonationFormQuestionRowV4[],
  buildRow: (partial: Partial<DonationFormQuestionRowV4>) => DonationFormQuestionRowV4,
  body: string
): DonationFormQuestionRowV4 => {
  const deletedQuestion = rows.find((row) => row.type === QuestionType.NotificationPhoneNumber && !row.isActiveOnForm);
  if (deletedQuestion) {
    return { ...deletedQuestion, isActiveOnForm: true, isDirty: true };
  }
  return buildRow({ body, type: QuestionType.NotificationPhoneNumber });
};

export const mapQuestionsToPreviewFormat = (
  questions: DonationFormQuestionRowV4[],
  locale: Locales
): QuestionOutput[] =>
  questions
    .filter((q) => q.isActiveOnForm)
    .map((question, index) => ({
      id: question.questionId ?? question.localId,
      type: question.type,
      questionFields: [{ id: v4(), locale, body: question.body }],
      countAlreadyAnswered: 0,
      required: question.required,
      choices: question.choices.map((c) => ({
        id: c.id ?? v4(),
        label: c.body,
        choiceFields: [{ id: v4(), locale, body: c.body }],
        maximum: null,
        sortIndex: c.sortIndex,
      })),
      hasRate: false,
      sortIndex: index,
    }));

export const buildPlaceholderDonorQuestions = (t: Translate, locale: Locales): QuestionOutput[] => [
  {
    id: v4(),
    type: QuestionType.Simple,
    questionFields: [
      {
        id: v4(),
        locale,
        body: t("dashboard", "liveFormEditor.stepQuestions.placeholder.howDidYouHearAboutOurCause"),
      },
    ],
    countAlreadyAnswered: 0,
    required: false,
    choices: [],
    hasRate: false,
    sortIndex: 0,
  },
];
